/**
 * previewRegistry
 *
 * Maps sectionConfig.headerPreview.component → a React component.
 *
 * Every preview component receives a standard set of props from the renderer:
 *   { data, fieldLayout, stores, storeData, extras }
 *
 * Simple previews only use `data` (and optionally `fieldLayout`).
 * Complex previews (Examinations, FollowUpReferral) pull from `stores` / `storeData` / `extras`.
 */

import React from "react";
import { ChiefComplaintTable } from "../../Sections/ChiefComplaintTable";
import { DiagnosisTable } from "../../Sections/DiagnosisTable";
import { AdultVaccinationTable } from "../../Sections/AdultVaccinationTable";
import { InvestigationsTable } from "../../Sections/InvestigationsTable";
import { ProceduresTable } from "../../Sections/ProceduresTable";
import { MedicationsTable } from "../../Sections/MedicationsTable";
import { AttachmentsFilePreview } from "../../Sections/AttachmentsFilePreview";
import {
  examinationsData,
  appointmentData,
  adviceData,
} from "../../Utils/SectionsDataMap";

// ─── standard previews (data + optional fieldLayout) ──────────────────────────

const ChiefComplaintTablePreview = ({ data }) => (
  <ChiefComplaintTable data={data} activeSection={false} />
);

const DiagnosisTablePreview = ({ data }) => (
  <DiagnosisTable data={data} activeSection={false} />
);

const AdultVaccinationTablePreview = ({ data, fieldLayout }) => (
  <AdultVaccinationTable data={data} activeSection={false} fieldLayout={fieldLayout} />
);

const InvestigationsTablePreview = ({ data }) => (
  <InvestigationsTable data={data} activeSection={false} />
);

const ProceduresTablePreview = ({ data }) => (
  <ProceduresTable data={data} activeSection={false} />
);

const MedicationsTablePreview = ({ data, fieldLayout }) => (
  <MedicationsTable data={data} activeSection={false} fieldLayout={fieldLayout} />
);

const AttachmentsFilePreviewComp = ({ data }) => (
  <AttachmentsFilePreview data={data} activeSection={false} />
);

// ─── special previews (multi-store / extra state) ─────────────────────────────

const ExaminationsPreview = ({ stores }) =>
  examinationsData(
    stores.vitals.data.some((item) => item.value !== ""),
    stores.vitals.data,
    stores.bloodGroup.data,
    stores.dentalData.data.length > 0,
    stores.dentalData.data,
    stores.otherExaminations.data,
  );

const FollowUpReferralPreview = ({ storeData, extras }) =>
  appointmentData(
    storeData.FollowUp,
    storeData.Referral,
    extras.followUpError,
    extras.referralError,
    storeData.IPDAdmissionReferral || [],
    extras.ipdReferralError || {},
  );

const MedicalAdvicePreview = ({ data }) => adviceData(data);

// ─── registry map ─────────────────────────────────────────────────────────────

export const previewRegistry = {
  ChiefComplaintTable: ChiefComplaintTablePreview,
  DiagnosisTable: DiagnosisTablePreview,
  AdultVaccinationTable: AdultVaccinationTablePreview,
  InvestigationsTable: InvestigationsTablePreview,
  ProceduresTable: ProceduresTablePreview,
  MedicationsTable: MedicationsTablePreview,
  AttachmentsFilePreview: AttachmentsFilePreviewComp,
  ExaminationsPreview,
  FollowUpReferralPreview,
  MedicalAdvicePreview,
};
